// Éléments visuels communs (charte du tableau de bord) : chiffres clés, panneaux, liens.
import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { tokens } from '../theme';

// Couleurs des séries de graphiques (séparation daltonisme validée) — jamais seules : légende ou étiquette à côté
export const SERIES = { production: '#3566A8', livraisons: '#C9850F', maturation: '#A9BCD8', livrable: '#12284C' };
// Fonds des pastilles d'icône
export const TINT = { navy: '#E3ECF7', amber: '#FBF0DA', green: '#E2F1E8', red: '#F8E1E2', gray: '#EEF1F6' };

/** Chiffre clé : icône, libellé, grande valeur, ligne d'explication. */
export const Kpi = ({ icon: Icon, label, value, sub, tint = 'gray', valueColor, onClick, compact = false, valueSize }) => (
  <Card variant="outlined" onClick={onClick}
    sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s', '&:hover': onClick ? { borderColor: tokens.navy } : {} }}>
    <CardContent sx={{ p: compact ? 1.75 : 2.25, '&:last-child': { pb: compact ? 1.75 : 2.25 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        {Icon && (
          <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: TINT[tint] || tint, color: tokens.navy, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        )}
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontFamily: tokens.mono, fontSize: valueSize || (compact ? 26 : 32), fontWeight: 600, lineHeight: 1.15, overflowWrap: 'anywhere', mt: compact ? 0.75 : 1.25, color: valueColor || tokens.ink }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

/** Panneau blanc à bordure fine, avec titre et action à droite. `flush` : contenu (tableau) collé aux bords. */
export const Panel = ({ title, action, children, flush = false, sx }) => (
  <Card variant="outlined" sx={sx}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', px: 2.25, pt: 2, pb: flush ? 1.5 : 0 }}>
        {title && <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{title}</Typography>}
        {action && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ml: 'auto' }}>{action}</Box>}
      </Box>
    )}
    {flush ? children : <CardContent sx={{ px: 2.25, pt: title || action ? 1.5 : 2.25 }}>{children}</CardContent>}
  </Card>
);

/** Lien d'action discret « Voir tout › ». */
export const LinkBtn = ({ children, onClick }) => (
  <Box component="button" onClick={onClick}
    sx={{ all: 'unset', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'primary.main', display: 'inline-flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}>
    {children}<ChevronRight sx={{ fontSize: 18 }} />
  </Box>
);

/** Barre de filtres au-dessus d'une liste. */
export const Toolbar = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>{children}</Box>
);

/** Légende d'une série : carré de couleur + libellé (+ valeur). */
export const Legend = ({ items }) => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 13, alignItems: 'center' }}>
    {items.map(([label, color, value]) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
        <span>{label}</span>{value !== undefined && <b style={{ fontFamily: tokens.mono }}>{value}</b>}
      </Box>
    ))}
  </Box>
);
